import React, { useEffect, useState } from "react";
import { useSearchParams, Link, Navigate, useNavigate } from "react-router-dom";
import { LiveKitRoom } from "@livekit/components-react";
import Room from "./room/Room";
import { fetchToken } from "./tokenService";
import { setActiveSessionId } from "./correlation";
import { LIVEKIT_WS_URL } from "./endpoints";
import { LIVEKIT_ROOM_OPTIONS } from "./roomOptions";
import { setStoredDisplayName, setStoredRoomId } from "./livekitStorage";
import MeetingLeftView from "./MeetingLeftView";
import "./LiveKitHome.css";
import { LiveKitSessionProvider } from "./LiveKitSessionContext";
import TelemetryBootstrap from "./TelemetryBootstrap";
import ClientLogSessionSetup from "./ClientLogSessionSetup";
import LocalObserverBootstrap from "./LocalObserverBootstrap";
import { reportClientLog, captureException } from "./clientLogReporter";

/**
 * Direct room link: /livekit/room?roomid=xxx&name=yyy
 * Fetches token and opens the meeting room directly (no join form).
 * Without query params, redirects to /livekit (join page) to enter room and name.
 * Optional query params: roomid, name, audio (true/false), video (true/false).
 */
export default function LiveKitRoomDirect() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roomId =
    searchParams.get("roomid") ||
    searchParams.get("roomId") ||
    searchParams.get("room") ||
    "";
  const name =
    searchParams.get("name") ||
    searchParams.get("nickname") ||
    searchParams.get("userId") ||
    "";
  const sessionId = (searchParams.get("sessionId") || "").trim();
  const audio = searchParams.get("audio");
  const video = searchParams.get("video");

  const audioEnabled = audio !== "false" && audio !== "0";
  const videoEnabled = video !== "false" && video !== "0";

  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leftMeeting, setLeftMeeting] = useState(null);
  const [fetchNonce, setFetchNonce] = useState(0);

  const roomTrimmed = roomId.trim();
  const nameTrimmed = name.trim();
  const hasParams = Boolean(roomTrimmed && nameTrimmed);

  useEffect(() => {
    if (!hasParams) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    // TI-316: forward the server-issued sessionId (URL is authority) on VSS calls.
    if (sessionId) setActiveSessionId(sessionId);

    fetchToken({ room: roomTrimmed, identity: nameTrimmed, role: "host" })
      .then((t) => {
        if (!cancelled) {
          setStoredDisplayName(nameTrimmed);
          setStoredRoomId(roomTrimmed);
          setToken(t);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          captureException(err, "fetch_livekit_token_direct");
          setError(err?.message || "Failed to get token");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hasParams, roomTrimmed, nameTrimmed, sessionId, fetchNonce]);

  // No room/name in URL – redirect to join page (after hooks so rules-of-hooks is satisfied)
  if (!hasParams) {
    return <Navigate to="/livekit" replace />;
  }

  if (loading) {
    return (
      <div className="livekit-direct-loading">
        <div className="livekit-direct-loading__spinner" />
        <p>Joining meeting…</p>
      </div>
    );
  }

  if (leftMeeting) {
    return (
      <MeetingLeftView
        roomId={leftMeeting.roomId}
        displayName={leftMeeting.displayName}
        onRejoinSameRoute={() => {
          setLeftMeeting(null);
          setFetchNonce((n) => n + 1);
        }}
        onBackToHome={() => {
          setLeftMeeting(null);
          navigate("/livekit");
        }}
      />
    );
  }

  if (error || !token) {
    return (
      <div className="livekit-direct-error">
        <h2>Could not join</h2>
        <p>{error}</p>
        <Link to="/livekit">Go to join page</Link>
      </div>
    );
  }

  return (
    <LiveKitSessionProvider accessToken={token}>
      <LiveKitRoom
        serverUrl={LIVEKIT_WS_URL}
        token={token}
        connect
        audio={
          audioEnabled
            ? {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
              }
            : false
        }
        video={videoEnabled ? { facingMode: "user" } : false}
        options={LIVEKIT_ROOM_OPTIONS}
        onMediaDeviceFailure={() => {
          console.warn(
            "LiveKit media device failure — check Safari permissions and HTTPS on iOS/iPadOS"
          );
        }}
        onError={(e) => {
          console.error("LiveKit error", e);
          reportClientLog("livekit_error", e?.message || String(e), {
            stack: e?.stack,
            context: { name: e?.name },
          });
        }}
        onDisconnected={() => {
          setLeftMeeting({ roomId: roomTrimmed, displayName: nameTrimmed });
          setToken(null);
        }}
        style={{ height: "100vh" }}
      >
        <TelemetryBootstrap />
        <ClientLogSessionSetup />
        <LocalObserverBootstrap />
        <Room />
      </LiveKitRoom>
    </LiveKitSessionProvider>
  );
}
